// The ONE hand-written file in the generated-client setup.
//
// It wires the app's runtime HTTP concerns into the generated hey-api `client` so every
// generated SDK call behaves EXACTLY like a call through the legacy axios `HttpService`.
// Everything under ./generated is auto-generated (never hand-edited); this file is the
// single seam a human maintains.
//
// What HttpService.js does today (and what this reproduces here, 1:1):
//   1. Authorization: Bearer <token>   (token read fresh from localStorage 'auth' per call)
//   2. X-Timezone header               (IANA tz, 'Asia/Jerusalem' fallback)
//   Base URL: REACT_APP_API_URL, joined as `${base}/${endpoint}`.
//
// HttpService attaches the token PER CALL (callers pass it as the 3rd arg); there is no
// axios interceptor. We centralize that here as a request interceptor on the generated
// client — the same interception model the mobile app uses — so no adapter has to thread
// a token through. This scopes the auth/tz injection to generated calls only; the app's
// global fetch and legacy HttpService are untouched.

import {client} from './generated/client.gen';

// Base URL — mirrors HttpService's `${REACT_APP_API_URL}`. The generated client appends
// the leading-slash paths (e.g. `/admin/food/check-name`) to this, producing the same
// absolute URL HttpService builds from `${REACT_APP_API_URL}/admin/food/check-name`.
export const getBaseUrl = () => process.env.REACT_APP_API_URL;

// Timezone header value — mirrors HttpService.getTimezone().
const getTimezone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch (e) {
    return 'Asia/Jerusalem';
  }
};

// --- Global 401 -> logout seam (optional) ---
// HttpService itself does no global logout today; call sites handle auth errors. We expose
// the same setter shape as the mobile client so a future global 401 handler can be wired
// without touching call sites. Unset = no-op (current admin behavior preserved).
let logoutHandler = null;
export const setApiLogoutHandler = handler => {
  logoutHandler = handler;
};

// --- Global 403 -> access-denied screen ---
// Distinct from 401 on purpose: 401 is "not logged in" (-> logout), 403 is "logged in, but not
// scoped to this row" (-> show the refusal screen). Registered by AccessDeniedProvider; unset =
// no-op, so tests and any non-React consumer of the client keep today's behavior.
let forbiddenHandler = null;
export const setApiForbiddenHandler = handler => {
  forbiddenHandler = handler;
};

/**
 * Report a 403 to the registered handler. Exported so the legacy axios instance in HttpService
 * (SSE + multipart uploads, which can't go through the generated client) reports to the SAME
 * handler — one screen, one seam, regardless of which transport the call used.
 */
export const notifyForbidden = url => {
  if (forbiddenHandler && !isAuthUrl(url) && !isEmbeddedWidgetUrl(url)) {
    forbiddenHandler();
  }
};

// True for the sign-in / forgot-password endpoints (server: @RequestMapping("/auth")). Parsed
// rather than substring-matched so a trainee named ".../auth/..." in a query string can't match.
// An unparseable url falls back to false: showing the screen is the safe default for a real 403.
const isAuthUrl = url => {
  if (!url) {
    return false;
  }
  try {
    return new URL(url, getBaseUrl()).pathname.startsWith('/auth/');
  } catch (e) {
    return false;
  }
};

// Endpoints whose 403 describes an EMBEDDED WIDGET, not the page.
//
// The app-wide screen is right when the 403 means "this page is not yours" — the page's own data
// was refused, so there is nothing left to show. It is wrong when the refusal is an expected,
// informational answer from a sub-widget while the page itself is perfectly accessible.
//
// `**/translation/**` is exactly that: the generic entity-translation API deliberately FAILS CLOSED
// for the types whose legacy tables have no safe ownership path (TEMPLATE, TEMPLATE_WORKOUT), so a
// 403 there is the server saying "this type is not translatable here" — which the translation panel
// renders as a sentence. Letting it through would replace the whole edit page with "no permission",
// and because the panel would then unmount and remount on its own retry, it would flap.
//
// Parsed, not substring-matched, for the same reason as isAuthUrl: a trainee or search term
// containing "/translation/" in a query string must not match.
const EMBEDDED_WIDGET_PATH = /^\/(admin|system|groupAdmin)\/translation(\/|$)/;

const isEmbeddedWidgetUrl = url => {
  if (!url) {
    return false;
  }
  try {
    return EMBEDDED_WIDGET_PATH.test(new URL(url, getBaseUrl()).pathname);
  } catch (e) {
    return false;
  }
};

let configured = false;

/**
 * Wire the generated hey-api client to behave like HttpService. Call ONCE at startup,
 * before any generated SDK call. Idempotent.
 */
export function configureApi() {
  if (configured) {
    return;
  }
  configured = true;

  client.setConfig({
    baseUrl: getBaseUrl(),
  });

  // (1)(2) Inject auth + timezone per request. Token is read fresh from localStorage on
  // every call, exactly matching what each service does today before passing it to
  // HttpService. Guarded by `has()` so an explicit per-call override still wins.
  client.interceptors.request.use(request => {
    if (!request.headers.has('Authorization')) {
      const token = localStorage.getItem('auth');
      if (token) {
        request.headers.set('Authorization', `Bearer ${token}`);
      }
    }
    if (!request.headers.has('X-Timezone')) {
      request.headers.set('X-Timezone', getTimezone());
    }
    return request;
  });

  // Optional global 401 -> logout, parity with the mobile client. No-op until a handler
  // is registered via setApiLogoutHandler (keeps today's call-site-driven behavior).
  client.interceptors.response.use(response => {
    if (response.status === 401 && logoutHandler) {
      logoutHandler();
    }
    // 403 -> app-wide access-denied screen. Skipped on the auth routes: a 403 from /auth/** is a
    // failed sign-in, which the login form reports inline — replacing the login page with "you
    // have no permission" would lock the user out of the only screen that can fix it.
    if (response.status === 403) {
      notifyForbidden(response.url);
    }
    return response;
  });
}
